import {Injectable} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {
  LookUpDTO,
  MotorQuoteInputDTO,
  MotorQuoteWarrantyDTO,
} from '../../models/MotorQuoteDTO.interfaces';
import {getEnumKeyByEnumValue} from '../../utils/enumKeyByValue';
import {HttpRequestInterface} from '../http/http-request.interface';
import {HttpService} from '../http/http.service';
import {PageTypeService} from '../page-type/page-type.service';
import {TranslationEnum} from '../../enums/translation.enum';
import {RecursiveMapping} from '../../class/custom-translation-loader';
@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private contentSubject: BehaviorSubject<Record<string, unknown> | null> =
    new BehaviorSubject<Record<string, unknown> | null>(null);
  public content$: Observable<Record<string, unknown> | null> = this.contentSubject.asObservable();
  private looksup?: LookUpDTO[];
  constructor(
    private httpService: HttpService,
    private pageTypeService: PageTypeService,
  ) {}

  getContent(): Observable<Record<string, unknown>> {
    const requestDefault: HttpRequestInterface = {
      endpoint: environment.defaultContent,
      responseType: 'json',
    };

    const requestAem: HttpRequestInterface = {
      endpoint: environment.aemContent,
      responseType: 'json',
    };

    return forkJoin([
      this.httpService.getOpt(requestDefault) as Observable<RecursiveMapping>,
      (<Observable<RecursiveMapping>>this.httpService.getOpt(requestAem)).pipe(
        catchError(() => {
          console.warn('AEM json not found');
          return of({});
        }),
      ) as Observable<RecursiveMapping>,
    ]).pipe(map(([defaultContent, aemContent]) => this.mergeContent(defaultContent, aemContent)));
  }

  private mergeContent(
    defaultContent: Record<string, unknown>,
    aemContent: Record<string, unknown>,
  ): Record<string, unknown> {
    return this.deepMerge(defaultContent, aemContent);
  }

  private deepMerge(
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ): Record<string, unknown> {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const sourceVal = source[key];
        const targetVal = target[key];

        if (this.isObject(sourceVal) && this.isObject(targetVal)) {
          target[key] = this.deepMerge(
            targetVal as Record<string, unknown>,
            sourceVal as Record<string, unknown>,
          );
        } else {
          target[key] = sourceVal;
        }
      }
    }
    return target;
  }

  private isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  setContent(content: Record<string, unknown>): void {
    this.contentSubject.next(content);
  }

  getContentValue(): Record<string, unknown> | null {
    return this.contentSubject.value;
  }

  getContentBasedOnPageType<T extends Record<string, string>>(enumType: T): Observable<keyof T> {
    return this.pageTypeService.typePage$.pipe(
      map((typePage) => {
        if (typePage === TranslationEnum.ENERGIA) {
          const energiaKey = Object.keys(enumType).find((key) => key.toLowerCase() === 'energia');
          if (energiaKey) {
            return energiaKey as keyof T;
          }
        }

        const type = getEnumKeyByEnumValue(enumType, typePage);
        if (type && type in enumType) {
          return type as keyof T;
        }

        return Object.keys(enumType)[0] as keyof T;
      }),
    );
  }

  mergeStringsBannerInfoStep1(objResponse: MotorQuoteInputDTO[], results: string[] = []): string {
    objResponse.forEach((field) => {
      if (field && field.label && field.displayValue)
        results.push(`${field.label}: ${field.displayValue}`);
    });
    return results.join(' - ');
  }

  getWarrantyValue(
    objResponse: MotorQuoteWarrantyDTO[],
    uuid: string,
    label: keyof MotorQuoteWarrantyDTO,
  ): string {
    const field = objResponse.find((field) => field.uuid === uuid);
    return field ? `${field[label]}` : '';
  }

  getVehicleValue(
    objResponse: MotorQuoteInputDTO,
    uuid: string,
    label: keyof MotorQuoteInputDTO,
  ): string {
    const field = objResponse.uuid === uuid ? objResponse : null;
    return field ? `${field[label]}` : '';
  }

  discountCalc(startPrice: number, endPrice: number): number {
    const discountPrice = startPrice - endPrice;
    const discountPercent = (discountPrice / startPrice) * 100;
    return Math.floor(Number(discountPercent.toFixed(2)));
  }

  replaceVariablesOnPopsOver(popOverDescription: string, data: MotorQuoteInputDTO[]): string {
    const variablePattern = /\{\{([^}]+)\}\}/g;
    return popOverDescription.replace(variablePattern, (match, p1) => {
      const cleanedPath = p1.trim().replace(/^\$root\.sessionData\./, '');
      const value = this.getValueFromPath(cleanedPath, data);
      return value !== undefined ? value : '';
    });
  }

  getValueFromPath(path: string, data: MotorQuoteInputDTO[]): any {
    const obj = data[0];
    return path.split('.').reduce((acc: any, part: string) => acc && acc[part], obj);
  }
}
